import { useMutation } from "@tanstack/react-query";
import {
  email,
  Form,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useFormState, type FieldValues } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import type { CrmDataProvider } from "../providers/types";
import type { DeveloperFiscalProfile } from "../types";
import { DeveloperFiscalDocuments } from "./DeveloperFiscalDocuments";
import { isValidRfc } from "./isValidRfc";

const FISCAL_ZIP_CODE_REGEX = /^\d{5}$/;

/**
 * react-hook-form validator for a developer's fiscal CP (Almikia Epic E1b,
 * TASK-034). The field is optional, so an empty value passes; a non-empty
 * value must be exactly 5 digits.
 */
const isValidFiscalZipCode = (value?: string | null) => {
  if (!value) return undefined;
  if (!FISCAL_ZIP_CODE_REGEX.test(value)) {
    return {
      message: "crm.validation.invalid_fiscal_zip_code",
      args: { _: "Fiscal zip code must be 5 digits" },
    };
  }
  return undefined;
};

type FiscalFormValues = Pick<
  DeveloperFiscalProfile,
  | "rfc"
  | "legal_name"
  | "tax_regime"
  | "fiscal_zip_code"
  | "cfdi_use"
  | "invoice_email"
>;

/**
 * "Datos fiscales" section on the developer show page (Almikia Epic E1b,
 * TASK-034). The caller (`DeveloperShow`) mounts this component only inside a
 * `<CanAccess resource="developer_fiscal_profiles">` gate, so the fetch below
 * never runs for a role other than direccion/contabilidad — `canAccess.ts`
 * mirrors the same two roles at the database RLS layer (TASK-027).
 *
 * `developer_fiscal_profiles` is 1:1 with `developers`, but a developer can
 * have no row yet (e.g. one created after the TASK-027 migration copy):
 * saving then creates the row instead of updating it.
 */
export const DeveloperFiscalSection = ({
  developerId,
}: {
  developerId: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data, isPending, refetch } = useGetList<DeveloperFiscalProfile>(
    "developer_fiscal_profiles",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
      filter: { developer_id: developerId },
    },
  );
  const profile = data?.[0];

  const { mutate, isPending: isSaving } = useMutation({
    mutationKey: ["developer-fiscal-save", developerId],
    mutationFn: async (values: FiscalFormValues) => {
      if (profile) {
        return dataProvider.update("developer_fiscal_profiles", {
          id: profile.id,
          data: values,
          previousData: profile,
        });
      }
      return dataProvider.create("developer_fiscal_profiles", {
        data: { ...values, developer_id: developerId },
      });
    },
    onSuccess: () => {
      refetch();
      notify("resources.developers.fiscal.updated", {
        messageArgs: { _: "Fiscal data updated" },
      });
    },
    onError: () => {
      notify("resources.developers.fiscal.update_error", {
        type: "error",
        messageArgs: { _: "An error occurred. Please try again" },
      });
    },
  });

  if (isPending) return null;

  return (
    <>
      <Separator />
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-medium">
          {translate("resources.developers.fiscal.title", {
            _: "Fiscal data",
          })}
        </h3>
        <Form
          onSubmit={(values: FieldValues) => mutate(values as FiscalFormValues)}
          record={profile ?? { developer_id: developerId }}
        >
          <DeveloperFiscalFields isSaving={isSaving} />
        </Form>
        <DeveloperFiscalDocuments developerId={developerId} />
      </div>
    </>
  );
};

const DeveloperFiscalFields = ({ isSaving }: { isSaving: boolean }) => {
  const translate = useTranslate();
  const { isDirty } = useFormState();

  return (
    <div className="flex flex-col gap-4">
      <TextInput source="rfc" validate={isValidRfc} helperText={false} />
      <TextInput source="legal_name" helperText={false} />
      <TextInput source="tax_regime" helperText={false} />
      <TextInput
        source="fiscal_zip_code"
        validate={isValidFiscalZipCode}
        helperText={false}
      />
      <TextInput source="cfdi_use" helperText={false} />
      <TextInput source="invoice_email" validate={email()} helperText={false} />
      <div className="flex justify-end">
        <Button type="submit" variant="outline" disabled={!isDirty || isSaving}>
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
